export default {
  // First-entry workspace welcome
  welcome: {
    title: 'Welcome to your company workspace',
    subtitle: 'Everything you need to hire on Tabbio lives here.',
    features: {
      companyPage: {
        title: 'Company Page',
        body: 'Build and publish your public company page with open roles, in minutes.',
      },
      applications: {
        title: 'Applications',
        body: 'Every application and talent-pool submission lands in one inbox.',
      },
      agent: {
        title: 'Your hiring agent',
        body: 'Chat to draft roles, polish your page, and manage candidates.',
      },
    },
    primaryCta: 'Open Company Page',
    skipCta: 'Not now',
    close: 'Close',
  },
  // Chat
  chat: {
    title: 'Chats',
    newChat: '+ New',
    emptyState: 'Start a conversation with your AI recruiting assistant',
    inputPlaceholder: 'Message Tabbio...',
    untitledThread: 'Untitled chat',
    deleteThreadTitle: 'Delete chat?',
    deleteThreadMessage: 'Delete “{{title}}”? This cannot be undone.',
    deleteThreadConfirm: 'Delete',
    deleteThreadMenuLabel: 'Delete {{title}}',
    deleteThreadSuccess: 'Chat deleted',
    deleteThreadFailed: 'Could not delete chat',
    deleteThreadFailedMessage: 'Your chat was restored. Please try again.',
    clearAllHistoryAction: 'Clear all chats',
    clearAllHistoryTitle: 'Clear all chats?',
    clearAllHistoryMessage: 'This will permanently delete all of your chats. This cannot be undone.',
    clearAllHistoryConfirm: 'Clear all',
    clearAllHistoryProgress: 'Clearing chats…',
    clearAllHistorySuccess: 'All chats cleared',
    clearAllHistoryPartial: 'Some chats could not be deleted',
    clearAllHistoryFailed: 'Could not clear chats',

    // Contextual chat intros for employer screens (web content panel).
    routeIntro: {
      applications: {
        m1: "You're in your Applications inbox. I can help you find candidates, summarize the pipeline, or move applicants forward.",
        m2: 'This is every application and talent-network submission for your company. Ask me to filter, summarize, or compare candidates.',
        m3: 'I can see your hiring pipeline here. Ask me who applied recently or which role needs attention.',
        chips: {
          latest: 'Newest applications',
          pipeline: 'Pipeline summary',
          role: 'Busiest role',
        },
      },
      companyPage: {
        m1: "You're editing your company page. I can help sharpen your bio, plan your blocks, or draft a job post.",
        m2: 'This is your public careers presence. Ask me what to improve before you share it.',
        m3: 'I can help you complete your company page: bio, FAQ, roles, and sharing.',
        chips: {
          improve: 'What should we improve?',
          bio: 'Sharpen our bio',
          jobs: 'Draft a job post',
        },
      },
    },
  },

  // Candidates
  candidates: {
    title: 'Candidates',
    subtitle: 'Browse and manage your candidate pipeline',
    detail: 'Candidate Profile',
    shortlist: 'Shortlist',
  },

  // Jobs
  jobs: {
    title: 'Job Postings',
    create: 'Create Job',
    applicants: 'Applicants',
  },

  // Company
  company: {
    title: 'Company Profile',
  },

  // Four-step job create/edit flow (PRD §7)
  jobFlow: {
    steps: {
      basics: {
        title: 'Job basics',
        subtitle: 'Title, team, and where the role is based. You can edit everything later.',
      },
      requirements: {
        title: 'Hiring requirements',
        subtitle: 'Describe the role and list what the right candidate brings.',
      },
      applications: {
        title: 'Applications',
        subtitle: 'Salary, candidate requirements, and the questions applicants answer.',
      },
      review: {
        title: 'Review and publish',
        subtitle: 'Check the details, choose where the job appears, then publish or save.',
      },
    },
    edit: {
      title: 'Edit role',
      loading: 'Loading this role…',
      notFoundTitle: 'Role not found',
      notFoundBody: 'This role is no longer available in the active workspace.',
    },
    aiBadge: 'AI draft — review every field',
    fields: {
      summary: 'Role summary',
      responsibilities: 'Responsibilities',
      requirements: 'Required qualifications',
      niceToHave: 'Nice to have',
      benefits: 'Benefits',
      seniority: 'Seniority',
      countryOptional: 'Country (optional)',
      cityOptional: 'Display region (optional)',
      candidateLocation: 'Candidate location',
      experienceYears: 'Relevant experience',
      language: 'Language',
      workEligibility: 'Work eligibility',
      nationality: 'Nationality',
    },
    placeholders: {
      summary: 'What the role is about, who it works with, and why it matters. At least 20 characters.',
      responsibility: 'Add a responsibility',
      requirement: 'Add a required qualification',
      niceToHave: 'Add a nice-to-have qualification',
      benefit: 'Add a benefit',
    },
    list: {
      add: 'Add item',
      remove: 'Remove item',
      moveUp: 'Move up',
      moveDown: 'Move down',
      empty: 'Nothing added yet.',
    },
    salary: {
      title: 'Salary',
      min: 'Minimum',
      max: 'Maximum',
      minPlaceholder: 'e.g. 15000',
      maxPlaceholder: 'e.g. 25000',
      currency: 'Currency',
      period: 'Period',
      showOnPost: 'Show salary on job post',
      showOnPostHint: 'Hidden salaries stay visible to your team only.',
    },
    requirementsGroup: {
      title: 'Candidate requirements',
    },
    workforce: {
      hint: 'Only set this where a workforce localisation quota requires it. Nationality is otherwise a protected attribute.',
      note: 'Policy / approval note',
      notePlaceholder: 'Reference the documented policy or approval that permits this requirement (at least 20 characters).',
      acknowledged: 'Documented and approved',
      acknowledgedHint: 'I confirm this requirement follows a legally reviewed, documented workflow.',
    },
    questions: {
      defaultsTitle: 'Default questions',
      defaultsHint: 'Enable the standard questions and adjust how candidates answer them.',
      customTitle: 'Your questions',
      customHint: 'Add role-specific questions candidates answer when applying.',
      addQuestion: 'Add question',
      answerType: 'Answer type',
      required: 'Required',
      promptPlaceholder: 'Write your question',
      options: 'Options',
      optionPlaceholder: 'Add an option',
      duplicate: 'Duplicate question',
      delete: 'Delete question',
      customLabel: 'Question {{number}}',
      types: {
        yes_no: 'Yes / No',
        multiple_choice: 'Multiple choice',
        short_answer: 'Short answer',
        long_answer: 'Long answer',
        number: 'Number',
      },
      defaults: {
        education: {
          prompt: 'What is your highest completed education?',
          options: {
            highSchool: 'High school',
            diploma: 'Diploma',
            bachelors: "Bachelor's",
            masters: "Master's",
            doctorate: 'Doctorate',
            other: 'Other',
          },
        },
        experienceYears: {
          prompt: 'How many years of relevant experience do you have?',
        },
        expectedSalary: {
          prompt: 'What is your expected monthly salary (AED)?',
        },
        availability: {
          prompt: 'How soon can you start?',
          options: {
            immediate: 'Immediate',
            oneMonth: '1 month',
            twoMonths: '2 months',
            threeMonths: '3 months',
            moreThanThree: 'More than 3 months',
          },
        },
      },
    },
    options: {
      seniority: {
        entry: 'Entry',
        mid: 'Mid',
        senior: 'Senior',
        lead: 'Lead',
        executive: 'Executive',
      },
      // Country-relative: {{country}}, {{city}} and {{region}} come from the
      // job's own location, so one set of strings covers every market.
      candidateLocation: {
        any: 'Anywhere',
        in_country: 'In {{country}}',
        in_city: 'In {{city}}',
        in_region: 'Across {{region}}',
        open_to_relocation: 'Open to relocating to {{country}}',
        open_to_relocation_any: 'Open to relocating',
        // Used when a stored value names a tier the job has no data for — an
        // imported role set to a city it never recorded. Better than rendering
        // the field empty and looking like the answer was lost.
        in_country_generic: "In the job's country",
        in_city_generic: "In the job's city",
        in_region_generic: "In the job's region",
      },
      experienceYears: {
        any: 'Any',
        '1_plus': '1+ years',
        '3_plus': '3+ years',
        '5_plus': '5+ years',
        '7_plus': '7+ years',
        '10_plus': '10+ years',
      },
      language: {
        any: 'Any',
        english: 'English',
        arabic: 'Arabic',
        english_and_arabic: 'English and Arabic',
        english_or_arabic: 'English or Arabic',
      },
      workEligibility: {
        any: 'Any',
        eligible: 'Already eligible to work there',
        eligible_or_sponsorship: 'Eligible or needs sponsorship',
        sponsorship_required: 'Sponsorship required',
      },
      // Asked only to support GCC workforce localisation quotas; every
      // non-'none' choice stays gated behind the policy note below.
      nationality: {
        none: 'No requirement',
        emirati: 'Emirati',
        saudi: 'Saudi',
        qatari: 'Qatari',
        kuwaiti: 'Kuwaiti',
        bahraini: 'Bahraini',
        omani: 'Omani',
        gcc: 'Any GCC national',
        other: 'Other',
      },
      period: {
        monthly: 'Monthly',
        yearly: 'Yearly',
      },
    },
    review: {
      edit: 'Edit',
      basicsTitle: 'Basics',
      contentTitle: 'Role content',
      applicationsTitle: 'Applications',
      untitled: 'Untitled role',
      salaryNotSet: 'Not set',
      salaryShown: 'Shown on post',
      salaryHidden: 'Hidden from post',
      noRequirements: 'Open to all candidates',
      questionsLabel: 'Application questions',
      questionCount_one: '{{count}} question',
      questionCount_other: '{{count}} questions',
      distributionTitle: 'Where this job appears',
    },
    distribution: {
      tabbio_and_company: {
        title: 'Tabbio job board + your company page',
        body: 'Listed in both places.',
        summary: 'Candidates will find this job on the Tabbio job board and on your company page.',
      },
      tabbio_only: {
        title: 'Tabbio job board only',
        body: 'Not listed on your company page.',
        summary: 'Candidates will find this job on the Tabbio job board. It will not appear on your company page.',
      },
      company_page_only: {
        title: 'Your company page only',
        body: 'Not listed on the Tabbio job board.',
        summary: 'Candidates will find this job on your company page. It will not appear on the Tabbio job board.',
      },
      hidden: {
        title: 'Not listed anywhere',
        body: 'Only your team can see it.',
        summary: 'Nobody outside your team can see this job.',
      },
    },
    confidential: {
      toggle: 'Hide your company name',
      hint: 'Candidates see the name below instead of your company name and logo.',
      labelField: 'Name candidates will see',
      labelPlaceholder: 'e.g. Leading UAE retail group',
      summary: 'Your company name and logo are replaced with "{{label}}".',
      unnamedLabel: 'your public label',
    },
    actions: {
      saveDraft: 'Save draft',
      publish: 'Publish',
      publishing: 'Publishing…',
      saveChanges: 'Save changes',
      saving: 'Saving…',
    },
    toasts: {
      publishedTitle: 'Job published',
      publishedBody: 'Your role is live and accepting applications.',
      draftTitle: 'Draft saved',
      draftBody: 'This role is saved privately. Publish it when ready.',
      savedTitle: 'Changes saved',
      savedBody: 'The role was updated.',
      offlineTitle: 'Role taken offline',
      offlineBody: 'New applications are stopped. Applicants are preserved.',
      reopenedTitle: 'Role reopened',
      reopenedBody: 'The role is live again with its previous distribution.',
      archivedTitle: 'Role archived',
      deletedTitle: 'Role deleted',
      deletedBody: 'It is off your page. Applicants and history are preserved.',
    },
    errors: {
      modeRequired: 'Describe the role or choose to post manually.',
      briefTooShort: 'Describe the role in at least 20 characters so AI can draft it.',
      titleRequired: 'Add a job title.',
      titleLength: 'The job title must be between 2 and 160 characters.',
      countryRequired: 'Choose a country, or set the role to remote.',
      cityRequired: 'Choose a city for on-site and hybrid roles.',
      summaryTooShort: 'Write a role summary of at least 20 characters.',
      responsibilitiesRequired: 'Add at least one responsibility.',
      requirementsRequired: 'Add at least one required qualification.',
      salaryInvalid: 'Salary must be a positive number.',
      salaryRange: 'Minimum salary must not exceed the maximum.',
      questionPromptRequired: 'Write the question prompt.',
      questionOptionsRequired: 'Multiple choice needs at least two different options.',
      workforceNoteRequired: 'Add a policy/approval note of at least 20 characters.',
      workforceAckRequired: 'Confirm the requirement is documented and approved.',
      confidentialDistribution: 'Hiding your company name needs the job listed on the Tabbio job board.',
      confidentialLabelRequired: 'Add a public label of 2–80 characters.',
      generateFailed: 'Could not generate this job post',
      submitFailed: 'Could not save this job',
      submitFailedHint: 'Please try again. Nothing was published.',
      companyRequired: 'Switch to a company workspace before posting a job.',
    },
    status: {
      draft: 'Draft',
      live: 'Live',
      offline: 'Offline',
      archived: 'Archived',
    },
    discard: {
      title: 'Discard your changes?',
      body: 'This role has unsaved changes. If you leave now they are lost.',
      confirm: 'Discard changes',
      keepEditing: 'Keep editing',
    },
    lifecycle: {
      edit: 'Edit',
      publish: 'Publish',
      takeOffline: 'Take offline',
      reopen: 'Reopen',
      archive: 'Archive',
      cancel: 'Cancel',
      offlineConfirmTitle: 'Take this role offline?',
      offlineConfirmBody: 'Candidates can no longer apply. Existing applicants stay in your pipeline.',
      offlineConfirmAction: 'Take offline',
      archiveConfirmTitle: 'Archive this role?',
      archiveConfirmBody: 'Archived roles leave everyday management. Applicants and history are preserved.',
      archiveConfirmAction: 'Archive',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete this role?',
      deleteConfirmBody:
        'It is removed from your page and from everyday management. Applicants and history are preserved.',
      deleteConfirmBodyLive:
        'This role is live. It is taken offline and removed from your page. Applicants and history are preserved.',
      deleteConfirmAction: 'Delete',
      // Live delete is offline-then-archive: when only the first half lands.
      // Shown in a dialog, not a toast — the body is too long for the HUD and
      // the employer has to act on it.
      deleteOfflinedOnlyTitle: 'Taken offline, not deleted',
      deleteOfflinedOnlyBody:
        'This role is off your page and no longer accepting applications, but it could not be deleted. Try again, or reopen it to put it back live.',
      deleteOfflinedOnlyRetry: 'Try again',
      deleteOfflinedOnlyDismiss: 'Not now',
      // The retry failed too. Same state, same surface — asking again would
      // loop, so the remaining route is the reopen the copy above offers.
      deleteOfflinedOnlyRetryFailedBody:
        'This role is still off your page and no longer accepting applications, and it still could not be deleted. Reopen it to put it back live, or leave it offline and try deleting it again later.',
      deleteOfflinedOnlyReopen: 'Reopen role',
      failedTitle: 'Could not update this role',
    },
  },

  // Company-wide Applications inbox (Phase 1 WP4, PRD §9)
  applications: {
    title: 'Applications',
    subtitle: 'Every application and talent-network submission for your company.',
    searchPlaceholder: 'Search candidates',
    countLabel_one: '{{count}} application',
    countLabel_other: '{{count}} applications',
    filters: {
      destinationAll: 'All destinations',
    },
    sort: {
      label: 'Sort',
      activity: 'Latest activity',
      newest: 'Newest',
    },
    status: {
      active: 'In talent network',
    },
    destinations: {
      talentNetwork: 'Join our talent network',
    },
    empty: {
      title: 'No applications yet',
      subtitle:
        'Applications arrive when candidates apply to your live roles or join your talent network from your public company page.',
      action: 'Open Company Page',
    },
    filteredEmpty: {
      title: 'No matching applications',
      subtitle:
        'Try a different search or clear the filters to see the full company list.',
    },
    error: {
      title: 'Could not load applications',
      subtitle:
        'Try again in a moment. Your company workspace still needs access to this inbox.',
    },
    submittedCv: 'Submitted CV',
    submittedCvAt: 'Submitted {{date}}',
    currentCvNotice: 'Current CV',
    // Two different facts, two different sentences: nothing was attached at
    // all, versus a CV was attached but no snapshot of it was stored.
    currentCvNoticeBody:
      'No CV was attached to this application — this is the candidate\u2019s live CV and can change at any time.',
    unsavedSnapshotNoticeBody:
      'No snapshot was saved when this application was submitted — this is the candidate\u2019s live CV and may have changed since they applied.',
    cvNoSections: 'This CV has no sections yet.',
    submittedAgo: 'Submitted {{time}}',
    detail: {
      talentTitle: 'Talent submission',
      destinationLabel: 'Destination',
      submittedLabel: 'Submitted',
      statusLabel: 'Status',
      consentTitle: 'Consent',
      consentBody:
        'This candidate agreed that your company may consider them for future opportunities.',
      consentAcceptedAt: 'Consented {{date}}',
      withdrawnNotice:
        'This candidate withdrew from your talent network. Their submission stays in your history, but they should not be contacted about new roles.',
      selectPrompt: {
        title: 'Select an application',
        subtitle:
          'Choose a candidate from the list to review their submitted CV, answers, and history.',
      },
    },
  },
} as const;
